import { Alert, AlertType, Input } from '@design';
import { ModalLarge } from '@terminal/components/modal';
import { appPaths } from '@terminal/routes/app-paths';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import styles from './CreateWatchlistModal.module.scss';

export interface CreateWatchlistModalProps {
  isLoading: boolean;
  onClose: () => void;
  onSubmit: (listName: string) => Promise<void>;
  clearError: () => void;
  watchlistErrorText?: string;
  hasExceededUsageLimit?: boolean;
}

export const CreateWatchlistModal = ({
  onClose,
  onSubmit,
  clearError,
  isLoading,
  watchlistErrorText,
  hasExceededUsageLimit = false,
}: CreateWatchlistModalProps) => {
  const { t } = useTranslation(['widget', 'watchlistManager']);
  const navigate = useNavigate();
  const [newListName, setNewListName] = useState('');

  const onNameInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewListName(e.target.value);
    clearError();
  };

  const onPrimaryBtnClick = async () => {
    await onSubmit(newListName);
  };

  const onBeforeClose = () => {
    setNewListName('');
    onClose();
  };

  const isPrimaryBtnDisabled =
    isLoading || !newListName.trim().length || hasExceededUsageLimit;

  return (
    <ModalLarge
      isOpen
      onClose={onBeforeClose}
      title={t('watchlistTrigger.modalTitle')}
      primaryBtnProps={{
        label: t('watchlistTrigger.saveBtnLabel'),
        disabled: isPrimaryBtnDisabled,
        onClick: () => {
          onPrimaryBtnClick();
        },
        'data-testid': 'submit-new-list-btn',
        isLoading,
      }}
      cancelBtnProps={{
        label: t('watchlistTrigger.cancelBtnLabel'),
        onClick: onBeforeClose,
      }}
      data-testid="create-new-list-modal"
    >
      <Input
        id="watchlist-name-input"
        name="watchlist-name-input"
        label={t('watchlistTrigger.inputLabel')}
        className={styles.styledInput}
        value={newListName}
        onChange={onNameInputChange}
        data-testid="watchlist-name-input"
        errorText={watchlistErrorText}
        hasError={!!watchlistErrorText}
        maxLength={30}
        rightIcon={t('watchlistTrigger.nameLength', {
          length: newListName.length,
        })}
        disabled={hasExceededUsageLimit}
      />
      {hasExceededUsageLimit && (
        <Alert
          className={styles.alert}
          variant="inline"
          type={AlertType.Warning}
          title={t('watchlistManager:watchlistLimitReached')}
          message={t('watchlistManager:upgradeToCreateMore')}
          primaryBtn={{
            variant: 'primary',
            onClick: () => {
              navigate(appPaths.settings.subscription);
            },
            label: t('watchlistManager:upgrade'),
            size: 'large',
          }}
        />
      )}
    </ModalLarge>
  );
};
